import { Autocomplete, Box, SxProps, TextField, Typography } from '@mui/material'
import { debounce, isEqual } from 'lodash'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { ProjectAcronym, useGetAcronymsQuery } from '../../store/redux/reducers/projectsSlice'
import { t } from 'i18next'
import { Option } from '../Form/Inputs/AutocompleteInput'
import { RequestProps } from '../../store/types'
import { filterOptions } from '../../store/utils/filterProjectsAcronymsOptions'
import { mediumDefaultPageSize } from '../../config/defaultPagination'

type AutocompleteDefaultProjectProps = {
  defaultProject: Option | null
  defaultProjectChange: (option: Option | null) => void
  sxProps?: SxProps
}

const AutocompleteDefaultProject = ({
  defaultProject,
  defaultProjectChange,
  sxProps,
}: AutocompleteDefaultProjectProps) => {
  const [projects, setProjects] = useState<ProjectAcronym[]>([])
  const [page, setPage] = useState(0)
  const [searchedAcronym, setSearchedAcronym] = useState('')
  const getAcronymProps = useCallback(() => {
    return {
      page: page,
      size: mediumDefaultPageSize,
      acronym: searchedAcronym,
      loggable: true,
    }
  }, [page, searchedAcronym])

  const [acronymProps, setAcronymProps] = useState<RequestProps>(getAcronymProps())
  const { data: projectsResponse } = useGetAcronymsQuery(acronymProps)

  useEffect(() => {
    if (projectsResponse) {
      setProjects(prevProjects => {
        const existingProjectIds = new Set(prevProjects.map(project => project.id))
        const newProjects = projectsResponse.content.filter(project => !existingProjectIds.has(project.id))
        return [...prevProjects, ...newProjects]
      })
    }
  }, [projectsResponse])

  const onSelectBottomScroll = useCallback(() => {
    if (!projectsResponse?.last) {
      handleFetchMoreData()
    }
  }, [projectsResponse?.last])

  const handleFetchMoreData = () => {
    setPage(prev => prev + 1)
  }

  useEffect(() => {
    setAcronymProps(getAcronymProps())
  }, [getAcronymProps, page, searchedAcronym])

  const debouncedOnSelectBottomScroll = useMemo(
    () =>
      debounce(() => {
        if (onSelectBottomScroll) {
          onSelectBottomScroll()
        }
      }, 100),
    [onSelectBottomScroll]
  )

  const onScroll = useCallback(
    (event: React.SyntheticEvent) => {
      const listboxNode = event.currentTarget as HTMLElement
      if (listboxNode.scrollTop + listboxNode.clientHeight >= listboxNode.scrollHeight - 1) {
        debouncedOnSelectBottomScroll()
      }
    },
    [debouncedOnSelectBottomScroll]
  )

  const timeoutRef = useRef<NodeJS.Timeout | null>(null)

  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const value = e.target.value
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
    }
    timeoutRef.current = setTimeout(() => {
      setSearchedAcronym(value)
    }, 500)
  }, [])

  const options = useMemo(() => {
    const mappedProjects = projects.map(project => ({ label: project.acronym, value: project.id }))
    const isDefaultProjectInOptions = defaultProject && !projects.some(project => project.id === defaultProject.value)

    return isDefaultProjectInOptions ? [...mappedProjects, defaultProject] : mappedProjects
  }, [defaultProject, projects])

  return (
    <Autocomplete
      sx={{ width: '60%', ...sxProps }}
      value={defaultProject || null}
      onChange={(_, newValue) => {
        defaultProjectChange(newValue)
      }}
      aria-label={t('components.settingsDialog.defaultProject')}
      options={options}
      getOptionLabel={option => option.label || ''}
      noOptionsText={t('page.worklog.form.projectsNotFound')}
      renderGroup={params => (
        <div key={params.key}>
          <Box sx={{ px: 1.5, py: 1 }}>
            <Typography variant="body1" sx={{ color: 'text.highEmphasis' }}>
              {params.group}
            </Typography>
          </Box>
          <Box sx={{ color: 'text.base' }}>{params.children}</Box>
        </div>
      )}
      renderInput={params => (
        <TextField
          {...(params as any)}
          onChange={handleInputChange}
          size={'small'}
          label={t('components.settingsDialog.defaultProject')}
        />
      )}
      ListboxProps={{
        style: { maxHeight: 250 },
        onScroll: onScroll,
      }}
      isOptionEqualToValue={(option, value) => {
        return isEqual(option.value, value.value)
      }}
      filterOptions={(options, state) => filterOptions(options, state, projects)}
    />
  )
}

export default AutocompleteDefaultProject
